/* The shared cart reads Shopify's rendered cart and keeps the current page in place. */
(() => {
  'use strict';
  const dialog = document.querySelector('[data-cart-drawer]');
  if (!dialog || typeof dialog.showModal !== 'function' || window.WonderbookCartDrawer) return;
  const content = dialog.querySelector('[data-cart-drawer-content]');
  const notice = dialog.querySelector('[data-cart-drawer-notice]');
  const status = dialog.querySelector('[data-cart-drawer-status]');
  const retry = dialog.querySelector('[data-cart-drawer-retry]');
  const cartUrl = new URL(dialog.dataset.cartUrl, window.location.origin);
  let opener = null, version = 0, controller = null, busy = false, writing = null;
  let ready = false, pendingRemoval = null, cartPageNeedsRefresh = false;
  const refreshCartPageIfNeeded = () => {
    if (cartPageNeedsRefresh && !dialog.open && window.location.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '')) window.location.reload();
  };

  // Reuse the exact flat cover artwork; draw only a transparent book shape.
  const recommendationBooks = new Map();
  const mountedRecommendationBooks = new WeakSet();
  function drawRecommendationBook(image) {
    const width = 400, height = width * image.naturalHeight / image.naturalWidth;
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = Math.ceil(height + 88);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const shape = (points, fill) => {
      ctx.beginPath(); points.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
      ctx.closePath(); ctx.fillStyle = fill; ctx.fill();
    };
    const bottom = 48 + height;
    shape([[52, 57], [470, 31], [470, bottom - 2], [52, bottom + 14]], '#516151');
    shape([[64, bottom], [464, bottom - 14], [470, bottom - 2], [52, bottom + 14]], '#f4f0e5');
    ctx.strokeStyle = '#c8c2b5'; ctx.lineWidth = .8;
    for (let n = 3; n < 12; n += 3) {
      ctx.beginPath(); ctx.moveTo(57, bottom + n); ctx.lineTo(468, bottom - 14 + n); ctx.stroke();
    }
    shape([[52, 57], [64, 48], [64, bottom], [52, bottom + 14]], '#63735e');
    ctx.save();
    // Equal X/Y scale preserves the original artwork's proportions, with a slight tilt.
    ctx.setTransform(width / image.naturalWidth, -14 / image.naturalWidth, 0, height / image.naturalHeight, 64, 48);
    ctx.drawImage(image, 0, 0); ctx.restore();
    const shade = ctx.createLinearGradient(64, 0, 82, 0);
    shade.addColorStop(0, '#00000028'); shade.addColorStop(.35, '#ffffff0a'); shade.addColorStop(1, '#00000000');
    shape([[64, 48], [82, 47.37], [82, bottom - .63], [64, bottom]], shade);
    return { src: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
  }
  function renderRecommendationBooks(root = document) {
    root.querySelectorAll('[data-cart-recommendation-book]').forEach(image => {
      if (mountedRecommendationBooks.has(image)) return;
      mountedRecommendationBooks.add(image);
      const source = image.currentSrc || image.src;
      const render = () => {
        if (!image.naturalWidth || !image.naturalHeight) return;
        try {
          let book = recommendationBooks.get(source);
          if (!book) { book = drawRecommendationBook(image); if (book) recommendationBooks.set(source, book); }
          if (!book || !image.isConnected) return;
          image.src = book.src; image.width = book.width; image.height = book.height;
          image.dataset.bookRender = 'png';
        } catch (_) { /* Keep the exact flat cover if canvas or CORS is unavailable. */ }
      };
      if (image.complete) render(); else image.addEventListener('load', render, { once: true });
    });
  }

  // Fit the rail to the books in view, so longer offscreen names leave no gap.
  const compactRails = new Map();
  function renderCompactRails(root = document) {
    compactRails.forEach((dispose, rail) => {
      if (!rail.isConnected) { dispose(); compactRails.delete(rail); }
    });
    root.querySelectorAll('[data-cart-recommendations-rail]').forEach(rail => {
      if (compactRails.has(rail)) return;
      let frame = 0;
      const fit = () => {
        frame = 0;
        if (!rail.isConnected || !rail.clientWidth) return;
        const bounds = rail.getBoundingClientRect();
        let height = 0;
        [...rail.children].forEach(card => {
          const rect = card.getBoundingClientRect();
          if (rect.right > bounds.left + 2 && rect.left < bounds.right - 2) height = Math.max(height, card.offsetHeight);
        });
        if (height) {
          const next = `${Math.ceil(height) + 4}px`;
          if (rail.style.height !== next) rail.style.height = next;
        }
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(fit); };
      const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
      observer?.observe(rail);
      [...rail.children].forEach(card => observer?.observe(card));
      rail.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.fonts?.ready.then(schedule);
      compactRails.set(rail, () => {
        observer?.disconnect(); cancelAnimationFrame(frame);
        rail.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule);
      });
      schedule();
    });
  }

  let deliveryDatesReady = null;
  const arrivalRequests = new Map();
  async function renderArrival(root = document) {
    const arrivals = [...root.querySelectorAll('[data-cart-arrival]')];
    if (!arrivals.length) return;
    try {
      if (!window.WonderpodsDeliveryDates && !deliveryDatesReady) {
        deliveryDatesReady = new Promise((resolve, reject) => {
          const script = document.createElement('script'); script.src = arrivals[0].dataset.cartDeliveryScript;
          script.onload = () => resolve(window.WonderpodsDeliveryDates); script.onerror = reject;
          document.head.append(script);
        }).catch(error => { deliveryDatesReady = null; throw error; });
      }
      const dates = window.WonderpodsDeliveryDates || await deliveryDatesReady;
      if (!dates) return;
      const getQuote = async format => {
        const key = dates.orderDay(new Date(), 'fr') + ':' + format;
        const previous = arrivalRequests.get(key);
        if (previous) { const value = await previous; if (dates.validateEstimate(value, 'fr', format)) return value; }
        const request = (async () => {
          const response = await fetch('https://printflow-tau.vercel.app/api/storefront/delivery-estimate?' + new URLSearchParams({format}), {mode:'cors',credentials:'omit',headers:{Accept:'application/json'},signal:AbortSignal.timeout(18000)});
          if (!response.ok) throw new Error('Delivery estimate unavailable');
          const body = await response.json(), value = dates.validateEstimate(body.estimate, 'fr', format);
          if (!value) throw new Error('Invalid estimate');
          return value;
        })();
        arrivalRequests.set(key, request);
        try { return await request; } catch (error) { arrivalRequests.delete(key); throw error; }
      };
      await Promise.all(arrivals.map(async node => {
        const formats = [...new Set((node.dataset.cartDeliveryFormats || '').split(',').map(v=>v.trim()).filter(Boolean))];
        if (!formats.length || formats.some(f=>!['softcover_5_5x5_5','softcover_8x8','hardcover_8x8','hardcover_11x11'].includes(f))) { node.hidden=true;return; }
        const quotes = await Promise.all(formats.map(getQuote));
        const estimate = {orderDate:quotes[0].orderDate,earliestArrival:quotes.map(q=>q.earliestArrival).sort().at(-1),latestArrival:quotes.map(q=>q.latestArrival).sort().at(-1)};
        if (node.isConnected) { node.querySelector('[data-cart-arrival-date]').textContent=dates.formatRange(estimate,'fr');node.hidden=false; }
      }));
    } catch (_) { arrivals.forEach(node=>{node.hidden=true;}); }
  }

  const message = (text = '', canRetry = false) => {
    status.textContent = text; notice.hidden = !text; retry.hidden = !canRetry;
  };
  const setBusy = (value) => {
    busy = value; content.inert = value || !ready;
    content.setAttribute('aria-busy', String(value));
  };
  const demoLink = (link) => link.dataset.wbDemoAction === 'cart' || link.getAttribute('href') === '#wb-demo-cart';
  const cartLink = (link) => {
    if (demoLink(link) || link.target === '_blank' || link.hasAttribute('download')) return false;
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === cartUrl.origin && url.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '') && !url.search && !url.hash;
    } catch (_) { return false; }
  };
  function mount() {
    document.querySelectorAll('a.wp-cart-link, a[data-photo-cart-link], [data-cart-drawer-open]').forEach(link => {
      if (!demoLink(link)) {
        link.setAttribute('aria-controls', dialog.id); link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-expanded', String(dialog.open));
      }
    });
  }
  function count(value) {
    if (!Number.isSafeInteger(value) || value < 0) return;
    document.body.dataset.cartItemCount = String(value);
    dialog.querySelector('[data-cart-drawer-count]').textContent = value ? `(${value})` : '';
    document.querySelectorAll('a.wp-cart-link, a[data-photo-cart-link]').forEach(link => {
      if (demoLink(link)) return;
      let badge = link.querySelector('.wp-cart-count:not([data-favorites-count])');
      if (!badge) { badge = document.createElement('span'); badge.className = 'wp-cart-count'; link.append(badge); }
      badge.textContent = String(value); badge.hidden = value === 0; badge.style.display = value === 0 ? 'none' : '';
      link.setAttribute('aria-label', `Panier, ${value} article(s)`);
    });
  }
  async function refresh() {
    if (!dialog.open) return;
    const current = ++version;
    controller?.abort(); controller = new AbortController();
    ready = false; setBusy(true); message('Chargement du panier…');
    try {
      const url = new URL(cartUrl); url.searchParams.set('section_id', 'main-cart');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Could not load cart');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = html.querySelector('.wp-cart-page');
      if (!section || (!section.querySelector('[data-cart-form]') && !section.querySelector('.wp-empty-state'))) throw new Error('Cart content unavailable');
      if (current !== version || !dialog.open) return;
      section.querySelectorAll('script, [data-cart-remove-dialog]').forEach(node => node.remove());
      // Avoid duplicate input/label IDs when the underlying page is the full cart.
      const ids = new Map();
      section.querySelectorAll('[id]').forEach(node => { const id = node.id; node.id = `drawer-${id}`; ids.set(id, node.id); });
      section.querySelectorAll('label[for]').forEach(node => { if (ids.has(node.htmlFor)) node.htmlFor = ids.get(node.htmlFor); });
      const offer = section.querySelector('[data-cart-multi-book-offer]');
      if (offer) section.querySelector('.wp-cart__items')?.append(offer);
      const keep = section.querySelector('.wp-cart__continue');
      if (keep) { keep.dataset.cartDrawerClose = ''; keep.textContent = 'Continuer à découvrir'; }
      const total = [...section.querySelectorAll('[data-cart-quantity]')].reduce((sum, input) => sum + Number(input.value), 0);
      const focusId = content.contains(document.activeElement) ? document.activeElement.id : '';
      const scrollTop = content.querySelector('.wp-cart__items')?.scrollTop || 0;
      content.replaceChildren(section); pendingRemoval = null; ready = true; count(total); message();
      renderRecommendationBooks(content); renderCompactRails(content); void renderArrival(content);
      const items = content.querySelector('.wp-cart__items');
      if (items) items.scrollTop = scrollTop;
      setBusy(false);
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      window.WonderbookCartPreviews?.refresh();
      window.dispatchEvent(new CustomEvent('wonderbook:cart-rendered', { detail: { root: content } }));
    } catch (error) {
      if (current === version && dialog.open && error.name !== 'AbortError') message('Impossible de charger votre panier. Veuillez réessayer.', true);
    } finally { if (current === version) setBusy(false); }
  }
  async function open(trigger) {
    opener = trigger || document.activeElement;
    if (!dialog.open) { dialog.showModal(); document.body.classList.add('wp-cart-drawer-open'); }
    mount();
    if (writing) await writing;
    if (dialog.open) await refresh();
  }
  async function update(updates) {
    if (busy || !ready || writing) return;
    setBusy(true); message('Mise à jour du panier…');
    const run = async () => {
      try {
        const response = await fetch(`${cartUrl.pathname.replace(/\/$/, '')}/update.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Impossible de mettre à jour votre panier. Veuillez réessayer.');
        cartPageNeedsRefresh = true; refreshCartPageIfNeeded();
        count(result.item_count);
        await refresh();
      } catch (error) {
        content.querySelectorAll('[data-cart-quantity]').forEach(input => { input.value = input.dataset.originalQuantity; });
        // A lost response may still represent a committed update. Require a fresh read before checkout.
        ready = false;
        if (dialog.open) message(error.message || 'Impossible de mettre à jour votre panier. Veuillez réessayer.', true);
      } finally { setBusy(false); }
    };
    writing = run();
    try { await writing; } finally { writing = null; }
  }
  function confirmRemoval(row, trigger) {
    if (busy || pendingRemoval) return;
    const input = row.querySelector('[data-cart-quantity]');
    if (!input) return;
    input.value = input.dataset.originalQuantity;
    const box = document.createElement('div'); box.className = 'wp-cart-drawer__removal';
    const label = document.createElement('p'); label.textContent = 'Retirer ce livre du panier ?';
    const keep = document.createElement('button'); keep.type = 'button'; keep.textContent = 'Garder dans le panier';
    const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Retirer le livre';
    box.setAttribute('role', 'group'); box.setAttribute('aria-label', label.textContent);
    box.append(label, keep, remove); row.append(box); pendingRemoval = box; keep.focus({ preventScroll: true });
    const checkout = content.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = true;
    keep.addEventListener('click', () => { box.remove(); pendingRemoval = null; if (checkout) checkout.disabled = checkout.hasAttribute("data-fr-purchase-hold"); trigger.focus({ preventScroll: true }); });
    remove.addEventListener('click', async () => {
      box.remove(); pendingRemoval = null;
      await update({ [input.name.slice(8, -1)]: 0 });
      if (dialog.open) dialog.querySelector('[data-cart-drawer-close]').focus({ preventScroll: true });
    });
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a');
    const trigger = event.target.closest('[data-cart-drawer-open]');
    if ((!link || !cartLink(link)) && (!trigger || demoLink(trigger))) return;
    event.preventDefault(); event.stopImmediatePropagation(); void open(trigger || link);
  }, true);
  dialog.addEventListener('click', event => {
    const carouselControl = event.target.closest('[data-cart-recommendation-direction]');
    if (carouselControl) {
      event.preventDefault();
      const rail = carouselControl.closest('.wp-cart-recommendations')?.querySelector('[data-cart-recommendations-rail]');
      if (rail) {
        const step = (rail.firstElementChild?.getBoundingClientRect().width || rail.clientWidth / 2) + (parseFloat(getComputedStyle(rail).columnGap) || 0);
        rail.scrollBy({ left: Number(carouselControl.dataset.cartRecommendationDirection) * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
      return;
    }
    const close = event.target.closest('[data-cart-drawer-close]');
    if (close) { event.preventDefault(); dialog.close(); return; }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); if (!busy && ready) confirmRemoval(remove.closest('[data-cart-item]'), remove); return; }
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
  });
  dialog.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || busy || !ready || !input.reportValidity()) return;
    if (input.valueAsNumber === 0) { confirmRemoval(input.closest('[data-cart-item]'), input); return; }
    if (input.value !== input.dataset.originalQuantity) void update({ [input.name.slice(8, -1)]: input.valueAsNumber });
  });
  dialog.addEventListener('submit', event => {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;
    if (busy || !ready || pendingRemoval) { event.preventDefault(); return; }
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    const inputs = [...form.querySelectorAll('[data-cart-quantity]')];
    const zero = inputs.find(input => input.valueAsNumber === 0);
    if (zero) { confirmRemoval(zero.closest('[data-cart-item]'), zero); return; }
    if (form.reportValidity()) void update(Object.fromEntries(inputs.map(input => [input.name.slice(8, -1), input.valueAsNumber])));
  });
  dialog.addEventListener('close', () => {
    if (dialog.open) return;
    version++; controller?.abort(); pendingRemoval?.remove(); pendingRemoval = null;
    document.body.classList.remove('wp-cart-drawer-open'); mount();
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    refreshCartPageIfNeeded();
  });
  retry.addEventListener('click', () => { if (!writing) void refresh(); });
  window.addEventListener('wonderbook:photo-cart-open', event => {
    let settings = {};
    try { settings = JSON.parse(document.querySelector('[data-personalization-v2-config]')?.textContent || '{}'); } catch (_) { /* Ordinary cart remains available. */ }
    if (settings.photoFlowDemo === true || event.defaultPrevented) return;
    event.preventDefault(); event.stopImmediatePropagation(); void open(document.activeElement);
  });
  window.addEventListener('wonderbook:photo-cart-updated', event => {
    if (event.detail?.demo === true) return;
    count(event.detail?.itemCount); if (dialog.open && !writing) void refresh();
  });
  window.addEventListener('shopify:section:load', mount);
  window.addEventListener('wonderbook:account-changed', () => { if (dialog.open) dialog.close(); content.replaceChildren(); ready = false; });
  window.addEventListener('pageshow', event => { mount(); if (event.persisted && dialog.open) void refresh(); });
  window.WonderbookCartDrawer = Object.freeze({ open, refresh, close: () => dialog.close() });
  renderRecommendationBooks();renderCompactRails();void renderArrival();
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)void renderArrival();});
  window.addEventListener('pageshow',()=>{void renderArrival();});
  window.setInterval(()=>{if(!document.hidden)void renderArrival();},60000);
  mount();count(Number(document.body.dataset.cartItemCount||0));
})();
